import type { PollInterpreter, PollMachineContext, PollMachineEvents } from 'components-machine';
import { useContext, useEffect, useState } from 'react';
import { PollContext } from '../../store/withContext';

export type AnyMachineState =
  | string
  | {
      [_P in string]: AnyMachineState;
    };
export function stateHasExactPath(value: AnyMachineState, path: string): boolean {
  return path.split('.').reduce(
    ({ pred, substate }, part) => {
      return typeof substate === 'object'
        ? { pred: pred && part in substate, substate: substate[part] ?? {} }
        : { pred: pred && substate === part, substate: {} };
    },
    { pred: true, substate: value },
  ).pred;
}

export function usePollInterpreterStatePredicate(interpreter: PollInterpreter, statePath: string) {
  const [isState, setIsState] = useState(stateHasExactPath(interpreter.getSnapshot().value, statePath));

  useEffect(() => {
    interpreter.subscribe(state => {
      setIsState(stateHasExactPath(state.value, statePath));
    });
  }, []);

  return isState;
}
export function usePollContextValue<T extends keyof PollMachineContext>(key: T): PollMachineContext[T] {
  const { interpreter } = useContext(PollContext);

  const [value, setValue] = useState(interpreter.getSnapshot().context[key]);

  useEffect(() => {
    interpreter.subscribe(state => {
      setValue(state.context[key]);
    });
  }, []);

  return value;
}
export function usePollStatePredicate(statePath: string): boolean {
  const { interpreter } = useContext(PollContext);
  return usePollInterpreterStatePredicate(interpreter, statePath);
}

export function usePollTransitionAbilityPredicate(transition: PollMachineEvents): boolean {
  const { interpreter } = useContext(PollContext);

  const [predicate, setPredicate] = useState(interpreter.getSnapshot().can(transition));

  useEffect(() => {
    interpreter.subscribe(() => {
      setPredicate(interpreter.getSnapshot().can(transition));
    });
  }, []);

  return predicate;
}
